import React, { Component, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { sprintf } from 'sprintf-js';
import scrollIntoView from 'scroll-into-view-if-needed';

import { toggleFilters } from '../../actions/AppActions';

import { Container, Row, Column } from '../../components/styleguide/grid';
import Icon from 'common/components/styleguide/Icon';
import TextIcon from 'common/components/styleguide/TextIcon';
import Button from '../../components/styleguide/Button';
import { withCssUnit } from '../../components/styleguide/utils/index';

import BreadcrumbsBar from '../../components/_partials/breadcrumbsBar/index';

import Listing from '../../components/category/listing/mobilonline';
import Filters from '../../components/category/filters/mobilonline';
import SubCategories from '../../components/category/SubCategories';
import ActiveFilters from '../../components/category/filters/_default/Active';
import PaginationWithPerPage from '../../components/category/PaginationWithPerPage';
import Sort from '../../components/category/Sort';

import Awards from '../../components/home/awards/mobilonline/index';
import FooterList from '../../components/product/footerList/mobilonline';
import Newsletter from '../../components/home/newsletter/mobilonline';

import NotFound from '../../components/NotFound';

import {
  getPageSelector,
  getPageDataSelector,
  getPageIsLoadingSelector
} from 'common/selectors/page';

import { getFiltersMenuIsOpenSelector } from 'common/selectors/index';

import { capfl, canUseDOM, getElementHeight } from 'common/utils/index';

import { fireGTMEvent, getGTMCategoryList } from 'common/utils/gtm';
import {
  PRODUCT_VIEW,
  PAGE_VIEW,
  RETARGETING_CATEGORIES
} from 'common/constants/GTMConstants';
import {
  ICON_CHEVRON_DOWN,
  ICON_CHEVRON_UP
} from 'common/constants/IconConstants';

import loader from 'assets/images/loader.svg';
import sadLogo from 'assets/images/mobilonline/emoji_sad.svg';
import { createTitleFromActiveFilters } from 'common/utils';
import { BLACKLISTED_FILTERS } from 'common/constants/global/mobilonline';

import SS from '../_style/index';
const StyledSection = SS.Section;
const StyledWrapper = SS.Wrapper;
const StyledOverlay = SS.Overlay;

const S = {};

S.WrapperNoPadding = styled(StyledWrapper)`
  && {
    padding: 0;
  }
`;

S.ListingWrapper = styled.div`
  padding-top: 20px;
  margin-bottom: 20px;
`;

S.ShowFiltersWrapper = styled.div`
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    display: none;
  }
`;

S.Button = styled(Button)`
  && {
    background: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.background', '#fafafa')};
    font-size: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.fontSize', '16px;')};
    color: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.color', '#000000')};
    font-weight: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.fontWeight', 'bold')};
    border-radius: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.borderRadius', '20px')};
    border: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.border', '0px')};
    justify-content: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.textAlign', 'center')};
    margin-top: 15px;
    text-align: ${({ theme }) =>
      _.get(theme, 'filters.showFiltersButton.textAlign', 'center')};
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

S.Icon = styled(Icon)`
  && {
    font-size: 30px;
  }
`;

S.Filters = styled.section`
  margin-top: 0;
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    margin-top: 50px;
  }
`;

S.Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => _.get(theme, 'colors.secondary')};
  min-height: 50px;
`;

S.DescriptionWrapper = styled.div`
  margin: 5px 0 30px 0;
`;

S.Description = styled.div`
  margin-bottom: 5px;
  color: ${({ theme }) => _.get(theme, 'colors.xdgray')};
  font-size: 0.85em;
  line-height: 1.3em;
  max-height: 80px;
  overflow: hidden;
  position: relative;
  padding-bottom: 30px;
  ${({ showGradient }) =>
  showGradient &&
    css`
      &:after {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 100%;
        width: 100%;
        content: '';
        background: linear-gradient(
          to top,
          rgba(255, 255, 255, 1) 20%,
          rgba(255, 255, 255, 0) 80%
        );
        pointer-events: none; /* so the text is still selectable */
      }
    `}

  ${({ showFullDescription }) =>
    showFullDescription &&
    css`
      max-height: none;
      &:after {
        background: transparent;
      }
    `}
`;

S.DescriptionToggleButton = styled.div`
  display: inline-block;
  color: ${({ theme }) => _.get(theme, 'colors.primary', '#000000')};
  font-size: 0.8em;
  cursor: pointer;
  line-height: 1.5em;

  & > i {
    margin-left: 5px;
    vertical-align: middle;
  }
`;

class Category extends Component {
  state = {
    showFullDescription: false,
    showToggleDescriptionButton: false
  };
  constructor(props) {
    super(props);
    this.listingSectionRef = React.createRef();
    this.categoryDescriptionRef = React.createRef();
  }
  scrollToListing = () => {
    if (canUseDOM()) {
      const node = ReactDOM.findDOMNode(this.listingSectionRef.current);
      if (node) {
        scrollIntoView(node, {
          scrollMode: 'if-needed',
          behavior: 'smooth',
          block: 'start',
          inline: 'center'
        });
      }
    }
  };
  onToggleFiltersMenuClick = e => {
    e.preventDefault();
    const { toggleFiltersMenu } = this.props;
    toggleFiltersMenu();
  };
  onToggleDescriptionClick = e => {
    e.preventDefault();
    this.setState({ showFullDescription: !this.state.showFullDescription });
  };
  componentDidMount() {
    const {
      products,
      breadcrumbs,
      gotActiveFilters,
      categoryData
    } = this.props;
    if (
      _.get(categoryData, 'hash', '') !== 'marketing-page' &&
      getElementHeight(this.categoryDescriptionRef.current) > 79)
    {
      this.setState({ showToggleDescriptionButton: true });
    }
    if (_.get(categoryData, 'hash', '') === 'marketing-page') {
      this.setState({ showFullDescription: true });
    }
    const list = getGTMCategoryList(categoryData, gotActiveFilters);
    let categories = breadcrumbs;
    let additionalData = null;
    if (_.get(categoryData, 'search', '')) {
      categories = [{ name: 'Search' }];
      additionalData = { searchString: _.get(categoryData, 'search', null) };
    }
    fireGTMEvent(PRODUCT_VIEW, products, list, additionalData);
    fireGTMEvent(RETARGETING_CATEGORIES, categories, list);
  }
  componentDidUpdate(prevProps) {
    const prevProducts = _.get(prevProps, 'products', {});
    const {
      products,
      breadcrumbs,
      gotActiveFilters,
      categoryData,
      filterIsOpen
    } = this.props;
    if (JSON.stringify(prevProducts) !== JSON.stringify(products)) {
      const list = getGTMCategoryList(categoryData, gotActiveFilters);
      let categories = breadcrumbs;
      let additionalData = null;
      if (_.get(categoryData, 'search', '')) {
        categories = [{ name: 'Search' }];
        additionalData = { searchString: _.get(categoryData, 'search', null) };
      }
      fireGTMEvent(PAGE_VIEW);
      fireGTMEvent(PRODUCT_VIEW, products, list, additionalData);
      fireGTMEvent(RETARGETING_CATEGORIES, categories, list);
    }
    if (!_.isEqual(prevProps.filterIsOpen, filterIsOpen)) {
      const bodyElement = _.first(document.getElementsByTagName('body'));
      if (filterIsOpen) {
        bodyElement.classList.add('fixed');
      } else {
        bodyElement.classList.remove('fixed');
      }
    }
  }
  shouldComponentUpdate(nextProps, nextState) {
    const {
      categoryData,
      gotFilters,
      gotProducts,
      gotPagination,
      isLoading,
      banners,
      products,
    } = this.props;
    const {
      showToggleDescriptionButton,
      showFullDescription
    } = this.state;
    if (!_.isEqual(categoryData, nextProps.categoryData)) return true;
    if (!_.isEqual(gotFilters, nextProps.gotFilters)) return true;
    if (!_.isEqual(gotProducts, nextProps.gotProducts)) return true;
    if (!_.isEqual(gotPagination, nextProps.gotPagination)) return true;
    if (!_.isEqual(isLoading, nextProps.isLoading)) return true;
    if (!_.isEqual(banners, nextProps.banners)) return true;
    if (!_.isEqual(showToggleDescriptionButton, nextState.showToggleDescriptionButton)) return true;
    if (!_.isEqual(showFullDescription, nextState.showFullDescription)) return true;
    if (!_.isEqual(JSON.stringify(products), JSON.stringify(nextProps.products))) return true;
    return false;
  }
  render() {
    const {
      t,
      meta,
      categoryData,
      gotFilters = false,
      gotProducts = false,
      gotPagination = false,
      isLoading = false,
      gotActiveFilters,
      activeFilters,
      categoryFilters
    } = this.props;
    const GTMCategoryList = getGTMCategoryList(categoryData, gotActiveFilters);
    const searchValue = _.get(categoryData, 'search', '');
    let title = '';
    if (searchValue) {
      title = sprintf(
        '%1$s: %2$s',
        capfl(t('searching phrase')),
        decodeURI(searchValue)
      );
    } else {
      title =
        _.get(categoryData, 'name', '') +
        ' ' +
        createTitleFromActiveFilters(
          activeFilters,
          categoryFilters,
          BLACKLISTED_FILTERS
        );
    }
    let description = _.get(categoryData, 'description', null);
    if (_.isEmpty(description)) {
      description = _.get(meta, 'content', '');
    }
    return (
      <S.WrapperNoPadding>
        <BreadcrumbsBar containerWidth={1800} withSameLevels={false} />
        <S.ShowFiltersWrapper>
          <Container maxWidth={1800}>
            <Column size={{ xs: 12 }}>
              <S.Button onClick={this.onToggleFiltersMenuClick} fullWidth>
                {capfl(t('parametric filter'))}
                <S.Icon className="nc-ic_keyboard_arrow_down_48px" />
              </S.Button>
            </Column>
          </Container>
        </S.ShowFiltersWrapper>
        <S.ListingWrapper ref={this.listingSectionRef}>
          <Container maxWidth={1800}>
            <Row>
              {gotProducts && gotFilters ? (
                <Column size={{ xs: 12, md: 3, lg: 2 }}>
                  <S.Filters>
                    <Filters />
                  </S.Filters>
                </Column>
              ) : (
                ''
              )}
              {gotProducts ? (
                <Column
                  size={{
                    xs: 12,
                    md: gotFilters ? 9 : 12,
                    lg: gotFilters ? 10 : 12
                  }}
                >
                  {title ? <S.Title>{title}</S.Title> : ''}
                  {description ? (
                    <S.DescriptionWrapper>
                      <S.Description
                        dangerouslySetInnerHTML={{ __html: `${description}` }}
                        showFullDescription={this.state.showFullDescription}
                        showGradient={this.state.showToggleDescriptionButton}
                        ref={this.categoryDescriptionRef}
                      />
                      {this.state.showToggleDescriptionButton ? (
                        <S.DescriptionToggleButton onClick={this.onToggleDescriptionClick}>
                          {t(this.state.showFullDescription ? 'show less' : 'show more')}
                          <Icon
                            className={(
                              this.state.showFullDescription
                                ? ICON_CHEVRON_UP
                                : ICON_CHEVRON_DOWN
                            )}
                          />
                        </S.DescriptionToggleButton>
                      ) : (
                        ''
                      )}
                    </S.DescriptionWrapper>
                  ) : (
                    ''
                  )}
                  <SubCategories />
                  <Sort />
                  <ActiveFilters />
                  <Listing
                    searchValue={searchValue}
                    GTMCategoryList={GTMCategoryList}
                  />
                  {gotPagination ? (
                    <PaginationWithPerPage afterClick={this.scrollToListing} />
                  ) : (
                    ''
                  )}
                </Column>
              ) : (
                <NotFound
                  title="can't find any products"
                  image={sadLogo}
                  columnSize={{
                    xs: 12,
                    md: gotFilters ? 9 : 12,
                    lg: gotFilters ? 10 : 12
                  }}
                />
              )}
            </Row>
          </Container>
        </S.ListingWrapper>
        {isLoading ? (
          <StyledOverlay>
            <img src={loader} alt="loader" />
          </StyledOverlay>
        ) : (
          ''
        )}
        <FooterList />
        <StyledSection>
          <Container>
            <Row>
              <Column size={{ xs: 12 }}>
                <Awards />
              </Column>
            </Row>
          </Container>
        </StyledSection>
        <Newsletter />
      </S.WrapperNoPadding>
    );
  }
}

const makeMapStateToProps = () => {
  const getPage = getPageSelector();
  const getPageData = getPageDataSelector();
  const getPageIsLoading = getPageIsLoadingSelector();
  const getFilterIsOpen = getFiltersMenuIsOpenSelector();
  const mapStateToProps = (state, props) => {
    const page = getPage(state, props);
    const pageData = getPageData(state, props);
    const isLoading = getPageIsLoading(state, props);
    const filter = _.get(pageData, 'filter');
    const activeFilters = _.get(pageData, 'activeFilters');
    return {
      isLoading,
      meta: _.get(page, 'meta'),
      categoryData: _.get(pageData, 'data'),
      gotProducts: !_.isEmpty(_.get(pageData, 'products')),
      products: _.get(pageData, 'products', {}),
      gotFilters: !_.isEmpty(filter),
      gotActiveFilters: !_.isEmpty(activeFilters),
      gotPagination: !_.isEmpty(_.get(pageData, 'pagination')),
      products: _.get(pageData, 'products', {}),
      breadcrumbs: _.get(pageData, 'breadcrumbs.categorical', {}),
      filterIsOpen: getFilterIsOpen(state, props),
      categoryFilters: _.get(pageData, 'categoryFilters'),
      activeFilters: activeFilters
    };
  };
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => {
  return {
    toggleFiltersMenu: () => {
      dispatch(toggleFilters());
    }
  };
};

const CategoryConnected = connect(
  makeMapStateToProps,
  mapDispatchToProps
)(Category);

export default withTranslation()(CategoryConnected);
